import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { PhysiciansService } from '@/services/physicians.service'
import { Physician } from '@/entities/physician'
import NotificationMessageService from '@/services/notificationmessage.service'

const service = new PhysiciansService()

const initialPhysician: Physician = {
  userId: '',
  firstName: '',
  lastName: '',
  email: '',
  city: '',
  isVerified: false,
}

export const usePhysiciansStore = defineStore('physicians', () => {
  // State
  const physicians = ref<Physician[]>([])
  const searchInput = ref('')
  const physician = ref<Physician>({ ...initialPhysician })

  // Getters
  const getSearchInput = computed(() => searchInput.value)
  const getPhysician = computed(() => physician.value)
  const getPhysicians = computed(() => {
    if (!searchInput.value) return physicians.value

    const search = searchInput.value.toLowerCase()
    return physicians.value.filter(p => {
      const firstName = (p.firstName ?? '').toLowerCase()
      const lastName  = (p.lastName ?? '').toLowerCase()
      const email     = (p.email ?? '').toLowerCase()
      const city      = (p.city ?? '').toLowerCase()

      return firstName.includes(search) ||
        lastName.includes(search) ||
        email.includes(search) ||
        city.includes(search)
    })
  })

  // Actions
  async function fetchPhysicians(forceUpdate = false) {
    try {
      physicians.value = await service.getPhysicians(forceUpdate)
    } catch (e) {
      NotificationMessageService.showError('Error while fetching physicians!')
    }
  }

  function setPhysician(newPhysician: Physician) {
    physician.value = newPhysician
  }

  function setSearchInput(input: string) {
    searchInput.value = input
  }

  function resetPhysician() {
    physician.value = { ...initialPhysician }
  }

  return {
    // State
    physicians,
    searchInput,
    physician,

    // Getters
    getPhysicians,
    getPhysician,
    getSearchInput,

    // Actions
    fetchPhysicians,
    setPhysician,
    setSearchInput,
    resetPhysician,
  }
})
